import { FileMappingSuggestionDto } from "api/api.typing";
import { FileMapping } from "./useFileMappingState";

export const NO_LANGUAGE_CODE = "NONE";
export const NO_SOURCE_FILE_ID = -1;

/**
 * Merges AI suggestions into existing mappings
 * Only adds suggestions for files that don't have mappings yet (preserves user edits)
 */
export const mergeSuggestions = (
  currentMappings: Map<string, FileMapping>,
  suggestions: FileMappingSuggestionDto[]
): Map<string, FileMapping> => {
  const newMappings = new Map(currentMappings);

  suggestions.forEach((suggestion) => {
    // Only add if not already present (preserve user edits)
    if (!newMappings.has(suggestion.uploadedFileName)) {
      newMappings.set(suggestion.uploadedFileName, {
        sourceFileIds: suggestion.sourceFile?.id ? [suggestion.sourceFile.id] : [],
        languageCodes: suggestion.languages.map((l) => l.code),
      });
    }
  });

  return newMappings;
};

/**
 * Applies batch selections to all selected files
 * Handles sentinel values (NO_SOURCE_FILE_ID, NO_LANGUAGE_CODE) for clearing
 */
export const applyBatchToMappings = (
  selectedFiles: Set<string>,
  currentMappings: Map<string, FileMapping>,
  batchSourceFileIds: number[],
  batchLanguages: string[]
): Map<string, FileMapping> => {
  const next = new Map(currentMappings);

  selectedFiles.forEach((fileName) => {
    const existing = next.get(fileName) ?? { languageCodes: [] };

    // Handle source files: NO_SOURCE_FILE_ID clears, empty keeps existing, values apply
    let sourceFileIds = existing.sourceFileIds;
    if (batchSourceFileIds.length > 0) {
      sourceFileIds = batchSourceFileIds.includes(NO_SOURCE_FILE_ID) ? [] : batchSourceFileIds;
    }

    // Handle languages: NO_LANGUAGE_CODE clears, empty array keeps existing, values apply
    const languageCodes = batchLanguages.includes(NO_LANGUAGE_CODE)
      ? []
      : batchLanguages.length
        ? batchLanguages
        : existing.languageCodes;

    next.set(fileName, { sourceFileIds, languageCodes });
  });

  return next;
};

/**
 * Validates that all files have complete mappings
 * A mapping is complete if it has at least one source file and one language
 */
export const validateMappings = (files: File[], mappings: Map<string, FileMapping>): boolean => {
  if (files.length === 0) return true;

  return files.every((file) => {
    const mapping = mappings.get(file.name);
    if (!mapping) return false;

    const hasValidSourceFiles =
      mapping.sourceFileIds && mapping.sourceFileIds.length > 0 && !mapping.sourceFileIds.includes(NO_SOURCE_FILE_ID);

    const hasValidLanguages = mapping.languageCodes.length > 0 && !mapping.languageCodes.includes(NO_LANGUAGE_CODE);

    return hasValidSourceFiles && hasValidLanguages;
  });
};
